import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatNumberIndian } from "../../../utilis/common-funtions";

const AGREEMENT_PERCENT = 0.09;
const GST_PERCENT = 0.05;

/**
 * Agreement Value calculation:
 * - 9% of Agreement Value (AV) / Sales Value
 * - 5% GST on the 9% value
 * - Total = 9% AV + 5% GST on 9% AV
 * - Paid Amount = Voucher/EOI payments + any booking form payments
 * - Final Payable = Total - Paid Amount
 */
export function getAgreementValueBreakdown(applicantData) {
  const agreementValue = Number(applicantData?.unitDetails?.totalAgreementValue) || 0;
  const ninePercentOfAV = Math.round(agreementValue * AGREEMENT_PERCENT);
  const gstOnNinePercent = Math.round(ninePercentOfAV * GST_PERCENT);
  const totalCalculatedAmount = ninePercentOfAV + gstOnNinePercent;

  const voucherSum =
    (Array.isArray(applicantData?.voucherPayments) &&
      applicantData.voucherPayments.reduce(
        (sum, p) => sum + (Number(p?.paidAmount) || 0),
        0
      )) ||
    0;
  const bookingPaymentsSum =
    (Array.isArray(applicantData?.payments) &&
      applicantData.payments.reduce(
        (sum, p) => sum + (Number(p?.paidAmount) || 0),
        0
      )) ||
    0;

  const groupPaymentsSum =
    (Array.isArray(applicantData?.groupPayments) &&
      applicantData.groupPayments.reduce(
        (sum, p) => sum + (Number(p?.paidAmount) || 0),
        0
      )) ||
    0;

  const voucherEoiPaid = voucherSum + bookingPaymentsSum + groupPaymentsSum;
  const finalPayableAmount = Math.max(0, totalCalculatedAmount - voucherEoiPaid);

  return {
    agreementValue,
    ninePercentOfAV,
    gstOnNinePercent,
    totalCalculatedAmount,
    voucherEoiPaid,
    finalPayableAmount,
    bookingPaymentsSum,
    voucherSum,
    groupPaymentsSum,
  };
}

/**
 * When provided, used as the sum of current form transactions so that Paid Amount
 * and Payable Amount update live when switching payment basis (e.g. Booking Amount → 9%)
 * without a page refresh.
 */
const AgreementValueCalculationCard = ({
  applicantData,
  payNowAmount,
  onPayNowAmountChange,
  renderPayNowSection,
  liveFormTransactionsSum,
}) => {
  const { t } = useTranslation("common");
  const breakdown = useMemo(
    () => getAgreementValueBreakdown(applicantData),
    [applicantData]
  );

  const hasAgreementValue = breakdown.agreementValue > 0;
  if (!hasAgreementValue) return null;

  const paidAmount =
    typeof liveFormTransactionsSum === "number"
      ? (breakdown.voucherEoiPaid - (breakdown.bookingPaymentsSum ?? 0)) + liveFormTransactionsSum
      : breakdown.voucherEoiPaid;
  const finalPayableAmount = Math.max(0, breakdown.totalCalculatedAmount - paidAmount);

  const rows = [
    {
      label: t("relandingPaymentStatus.label.agreementValue"),
      value: breakdown.agreementValue,
    },
    {
      label: t("relandingPaymentStatus.label.agreementPercentage"),
      value: breakdown.ninePercentOfAV,
    },
    {
      label: t("relandingPaymentStatus.label.gstCharges"),
      value: breakdown.gstOnNinePercent,
    },
    {
      label: t("relandingPaymentStatus.label.totalAmount"),
      value: breakdown.totalCalculatedAmount,
    },
    {
      label: t("relandingPaymentStatus.label.paidAmount"),
      value: paidAmount,
    },
    {
      label: t("relandingPaymentStatus.label.payableAmount"),
      value: finalPayableAmount,
      highlight: true,
    },
  ];

  return (
    <Box
      sx={{
        border: "1px solid rgba(26, 64, 125, 0.3)",
        borderRadius: 1,
        p: 3,
        mb: 2.5,
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontSize: 16,
          fontWeight: 600,
          color: "#1a407d",
          mb: 2,
        }}
      >
        {t("relandingPaymentStatus.label.agreementValueDetails")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {rows.map((row) => (
          <Box
            key={row.label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              ...(row.highlight && {
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid rgba(26, 64, 125, 0.2)",
              }),
            }}
          >
            <Typography variant="body2" sx={{ color: "#1C252E", fontWeight: 400 }}>
              {row.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: row.highlight ? 700 : 600,
                color: row.highlight ? "#1a407d" : "#1C252E",
                fontSize: row.highlight ? 16 : 14,
              }}
            >
              ₹ {formatNumberIndian(row.value)}
            </Typography>
          </Box>
        ))}
      </Box>
      {typeof renderPayNowSection === "function" &&
        renderPayNowSection({
          finalPayableAmount,
          payNowAmount,
          onPayNowAmountChange,
        })}
    </Box>
  );
};

AgreementValueCalculationCard.propTypes = {
  applicantData: PropTypes.object,
  payNowAmount: PropTypes.string,
  onPayNowAmountChange: PropTypes.func,
  renderPayNowSection: PropTypes.func,
  liveFormTransactionsSum: PropTypes.number,
};

export default AgreementValueCalculationCard;
